<template>
  <div class="mask" @click.self="onClose">
    <div class="sort-dialog">
      <!-- ヘッダー -->
      <div class="sort-header">
        <span class="sort-header-title">{{ $t("homePage.novelList.toolbar.sortDialogTitle") }}</span>
        <button class="sort-close-button" @click="onClose">
          <span>&times;</span>
        </button>
      </div>

      <!-- コンテンツ -->
      <div class="sort-body">
        <!-- 並び替え基準 -->
        <div class="sort-section">
          <h4 class="sort-section-title">{{ $t("homePage.novelList.toolbar.sortCriteria") }}</h4>
          <div class="sort-option-list">
            <button
              v-for="option in sortKeyOptions"
              :key="option.value"
              class="sort-option-item"
              @click="selectedSortKey = option.value"
            >
              <span class="sort-check" :class="{ active: selectedSortKey === option.value }">✓</span>
              <span class="sort-option-label">{{ option.label }}</span>
            </button>
          </div>
        </div>

        <!-- 並び順（指定なし以外で表示） -->
        <div class="sort-section" :style="{ visibility: selectedSortKey !== '' ? 'visible' : 'hidden' }">
          <h4 class="sort-section-title">{{ $t("homePage.novelList.toolbar.sortDirection") }}</h4>
          <div class="sort-option-list">
            <button class="sort-option-item" @click="selectedSortOrder = 'asc'">
              <span class="sort-radio" :class="{ active: selectedSortOrder === 'asc' }" />
              <span class="sort-option-label">{{ ascLabel }}</span>
            </button>
            <button class="sort-option-item" @click="selectedSortOrder = 'desc'">
              <span class="sort-radio" :class="{ active: selectedSortOrder === 'desc' }" />
              <span class="sort-option-label">{{ descLabel }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- アクション -->
      <div class="sort-actions">
        <button class="sort-close-action" @click="onClose">
          {{ $t("common.close") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";

export interface NovelSortDialogProps {
  sortKey: string;
  sortOrder: string;
}

export interface NovelSortDialogResult {
  sortKey: string;
  sortOrder: string;
}

interface SortKeyOption {
  value: string;
  label: string;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    sortKey: {
      type: String,
      default: "",
    },
    sortOrder: {
      type: String,
      default: "asc",
    },
    onChange: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      selectedSortKey: this.sortKey,
      selectedSortOrder: this.sortOrder,
    };
  },

  computed: {
    sortKeyOptions() {
      return [
        { value: "createdAt", label: this.$t("homePage.novelList.toolbar.sortCreatedAt") as string },
        { value: "updatedAt", label: this.$t("homePage.novelList.toolbar.sortUpdatedAt") as string },
        { value: "title", label: this.$t("homePage.novelList.toolbar.sortTitle") as string },
        { value: "", label: this.$t("homePage.novelList.toolbar.sortNone") as string },
      ];
    },
    descLabel() {
      if (this.selectedSortKey === "title") {
        return this.$t("homePage.novelList.toolbar.sortOrderReverseAlphabetical") as string;
      }
      return this.$t("homePage.novelList.toolbar.sortOrderDesc") as string;
    },
    ascLabel() {
      if (this.selectedSortKey === "title") {
        return this.$t("homePage.novelList.toolbar.sortOrderAlphabetical") as string;
      }
      return this.$t("homePage.novelList.toolbar.sortOrderAsc") as string;
    },
  },

  watch: {
    selectedSortKey: "emitChange",
    selectedSortOrder: "emitChange",
  },

  methods: {
    emitChange() {
      if (this.onChange) {
        this.onChange({ sortKey: this.selectedSortKey, sortOrder: this.selectedSortOrder });
      }
    },
    onClose() {
      this.$close(null);
    },
  },
});

interface Props {
  sortKey: string;
  sortOrder: string;
  onChange: ((result: NovelSortDialogResult) => void) | null;
}

interface Data {
  selectedSortKey: string;
  selectedSortOrder: string;
}

interface Computed {
  sortKeyOptions: SortKeyOption[];
  descLabel: string;
  ascLabel: string;
}

interface Methods {
  emitChange(): void;
  onClose(): void;
}
</script>

<style lang="scss" scoped>
.mask {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  z-index: 9000;
  align-items: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.sort-dialog {
  background-color: var(--color-background-normal);
  border-radius: var(--radius-md);
  width: 340px;
  max-width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

/* ヘッダー */
.sort-header {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 20px 24px 12px;
}

.sort-header-title {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
}

.sort-close-button {
  position: absolute;
  right: 16px;
  top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;

  span {
    font-size: var(--font-size-xl);
    line-height: 1;
    color: var(--color-text-normal);
  }
}

/* コンテンツ */
.sort-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 24px 0;
}

.sort-section {
  margin-bottom: 20px;
}

.sort-section-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  margin: 0 0 4px;
}

/* オプションリスト */
.sort-option-list {
  display: flex;
  flex-direction: column;
}

.sort-option-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
}

/* チェックマーク（並び替え基準） */
.sort-check {
  width: 20px;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  color: transparent;

  &.active {
    color: #4caf50;
  }
}

/* ラジオボタン（並び替え順） */
.sort-radio {
  width: 20px;
  height: 20px;
  border-radius: var(--radius-circle);
  border: 2px solid var(--color-border);
  box-sizing: border-box;
  position: relative;
  flex-shrink: 0;

  &.active {
    border-color: var(--color-primary);

    &::after {
      content: "";
      position: absolute;
      top: 3px;
      left: 3px;
      width: 10px;
      height: 10px;
      border-radius: var(--radius-circle);
      background-color: var(--color-primary);
    }
  }
}

.sort-option-label {
  font-size: var(--font-size-sm);
  color: var(--color-text-normal);
}

/* アクション */
.sort-actions {
  padding: 12px 24px 20px;
}

.sort-close-action {
  width: 100%;
  padding: 10px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-normal);
  background-color: var(--color-surface-muted);
  border: none;
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }
}
</style>
